import type { ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBarsFilter,
    faChevronDown,
} from "@fortawesome/pro-regular-svg-icons";
import { cn } from "~/lib/utils";
import { ToggleRow } from "./ToggleRow";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/ui/select";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";
import { AggregateFunctions } from "~/modules/filters/types";
import { DistributionType, DistributionTypes } from "~/types";
import { AGGREGATE_FUNCTION_LABELS } from "./AggregateFunctionControls";
import { ButtonGroup } from "~/components/ButtonGroup";
import { faLock } from "@fortawesome/pro-solid-svg-icons";

// // // //

/**
 * Every control in this panel takes the same shape: whether it's rendered at
 * all, its current value, and a change handler. `defaultValue` is optional and
 * only feeds the header badge — a control without one never counts as "set".
 */
export interface MoreControlsState<T> {
    enabled: boolean;
    value: T;
    onChange: (updatedValue: T) => void;
    /** The value this control sits at before a user touches it. */
    defaultValue?: T;
}

export interface MoreControlsSegmentOption<T extends string> {
    label: string;
    value: T;
    tooltip?: string;
    loginRequired?: boolean;
}

export interface MoreControlsSegmentedState<
    T extends string,
> extends MoreControlsState<T> {
    options: MoreControlsSegmentOption<T>[];
}

/** The three distribution segments, matching `DistributionSelect`'s labels. */
export const DISTRIBUTION_SEGMENT_OPTIONS: MoreControlsSegmentOption<DistributionType>[] =
    [
        { label: "Distributed", value: DistributionTypes.Distributed },
        { label: "Represented", value: DistributionTypes.Represented },
        { label: "All", value: DistributionTypes.All },
    ];

// // // //

/**
 * MoreControlsPanel
 * The mobile "More Controls" disclosure — a popover holding the
 * timeseries controls that don't earn a permanent slot on a phone-width card.
 * The controls float over the chart rather than expanding inline, so opening
 * them doesn't shift everything below down the page.
 *
 * Fully controlled and stateless, including its open/closed state. Each control
 * is optional; omitting it (or passing `enabled: false`) leaves it unrendered,
 * and the whole component renders nothing when no control is enabled.
 */
export function MoreControlsPanel(props: {
    /** Whether the user is logged in. */
    isLoggedIn: boolean;
    /** Whether the popover is open. */
    open: boolean;
    onOpenChange: (open: boolean) => void;
    disabled?: boolean;
    /** "Asset Type" — distributed / represented / all. */
    distribution?: MoreControlsSegmentedState<DistributionType>;
    /** A page-supplied segment select, e.g. the stocks page's "Underlying Type". */
    segment?: MoreControlsSegmentedState<string> & { label: string };
    /** "Aggregate" — the function timeseries values are rolled up with. */
    aggregateFunction?: MoreControlsState<AggregateFunctions> & {
        allowedOptions: AggregateFunctions[];
    };
    /** "Show % of Total" — percent-of-total vs. absolute values (the stacked control). */
    scale?: MoreControlsState<boolean>;
    /** "Include Stablecoins". */
    stablecoins?: MoreControlsState<boolean>;
}) {
    const {
        open,
        onOpenChange,
        isLoggedIn,
        disabled = false,
        distribution,
        segment,
        aggregateFunction,
        scale,
        stablecoins,
    } = props;

    const distributionControl = distribution?.enabled ? (
        <SegmentedControl
            label="Asset Type"
            value={distribution.value}
            options={distribution.options}
            isLoggedIn={isLoggedIn}
            disabled={disabled}
            onChange={distribution.onChange}
        />
    ) : null;

    const segmentControl = segment?.enabled ? (
        <SegmentedControl
            label={segment.label}
            value={segment.value}
            isLoggedIn={isLoggedIn}
            options={segment.options}
            disabled={disabled}
            onChange={segment.onChange}
        />
    ) : null;

    const aggregateFunctionControl = aggregateFunction?.enabled ? (
        <ControlLabel label="Aggregate">
            <Select
                value={aggregateFunction.value}
                disabled={disabled}
                onValueChange={(updatedValue) => {
                    aggregateFunction.onChange(
                        updatedValue as AggregateFunctions,
                    );
                }}
            >
                <SelectTrigger
                    className="h-9 cursor-pointer bg-card text-sm hover:bg-background-hover focus:ring-0 focus:ring-offset-0"
                    data-testid="more-controls-panel--aggregate-function"
                >
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {aggregateFunction.allowedOptions.map((option) => (
                        <SelectItem
                            key={option}
                            value={option}
                            className="cursor-pointer text-sm"
                        >
                            {AGGREGATE_FUNCTION_LABELS[option]}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </ControlLabel>
    ) : null;

    const scaleControl = scale?.enabled ? (
        // Same label + switch as StackedControls renders outside the panel
        <ToggleRow
            label="Show % of Total"
            checked={scale.value}
            disabled={disabled}
            onChange={scale.onChange}
        />
    ) : null;

    const stablecoinsControl = stablecoins?.enabled ? (
        <ToggleRow
            label="Include Stablecoins"
            description="Stablecoins, cash and cash-equivalents"
            checked={stablecoins.value}
            disabled={disabled}
            onChange={stablecoins.onChange}
        />
    ) : null;

    const enabledControls = [
        distributionControl,
        segmentControl,
        aggregateFunctionControl,
        scaleControl,
        stablecoinsControl,
    ].filter((control) => control !== null);

    // Nothing to scope or aggregate — don't render the disclosure at all.
    if (enabledControls.length === 0) {
        return null;
    }

    // The badge counts controls the user has moved off their default. With
    // everything at rest it falls back to how many controls live in here, so
    // the panel advertises what it holds rather than reading as "0".
    const changedCount = [
        distribution,
        segment,
        aggregateFunction,
        scale,
        stablecoins,
    ].filter(isChangedFromDefault).length;

    const badgeLabel =
        changedCount === 0
            ? `${enabledControls.length}`
            : `${changedCount} set`;

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    disabled={disabled}
                    className="flex h-8 w-full cursor-pointer select-none items-center gap-2 rounded border px-2.5 transition-colors disabled:cursor-not-allowed disabled:opacity-50 hover:bg-background-hover"
                    data-testid="more-controls-panel--trigger"
                >
                    <FontAwesomeIcon
                        icon={faBarsFilter}
                        className="h-3.5 text-primary"
                    />

                    <span className="text-sm font-medium text-foreground">
                        More Controls
                    </span>

                    <span
                        className={cn(
                            "grid h-4.5 min-w-4.5 place-items-center rounded-full px-1.5 text-[10px] font-bold",
                            {
                                "bg-primary text-primary-foreground":
                                    changedCount > 0,
                                "bg-primary/10 text-primary":
                                    changedCount === 0,
                            },
                        )}
                    >
                        {badgeLabel}
                    </span>

                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={cn(
                            "ml-auto h-3 text-muted-foreground transition-transform duration-200",
                            {
                                "rotate-180": open,
                            },
                        )}
                    />
                </button>
            </PopoverTrigger>

            {/* Floats over the chart rather than pushing it down the page. */}
            <PopoverContent
                align="start"
                // Matches the trigger's width so the panel reads as an
                // extension of it rather than a floating card of its own.
                className="flex w-[var(--radix-popover-trigger-width)] flex-col gap-2.5 p-2.5"
                data-testid="more-controls-panel--content"
            >
                {distributionControl}

                {segmentControl}

                {aggregateFunctionControl}

                {scaleControl}

                {stablecoinsControl}
            </PopoverContent>
        </Popover>
    );
}

// // // //

/**
 * Whether an enabled control has been moved off the default it was handed.
 * Controls without a `defaultValue` have no baseline to compare against, so
 * they never count toward the badge.
 */
function isChangedFromDefault(control?: {
    enabled: boolean;
    value: unknown;
    defaultValue?: unknown;
}): boolean {
    return (
        !!control?.enabled &&
        control.defaultValue !== undefined &&
        control.value !== control.defaultValue
    );
}

/** Stacks a control under its section label. */
function ControlLabel(props: { label: string; children: ReactNode }) {
    const { label, children } = props;

    return (
        <div className="flex min-w-0 flex-col gap-1">
            <span className="text-xs text-secondary-foreground select-none">
                {label}
            </span>
            {children}
        </div>
    );
}

/**
 * An equal-width segmented select, sized to fill the panel. Styled to match
 * TabSelectV2, but laid out to fill its column rather than hug its options.
 */
function SegmentedControl<T extends string>(props: {
    label: string;
    value: T;
    isLoggedIn: boolean;
    options: MoreControlsSegmentOption<T>[];
    disabled?: boolean;
    onChange: (updatedValue: T) => void;
}) {
    const {
        label,
        value,
        isLoggedIn,
        options,
        disabled = false,
        onChange,
    } = props;

    return (
        <ControlLabel label={label}>
            <ButtonGroup
                size="sm"
                fullWidth
                options={options.map((v) => ({
                    label: v.label,
                    value: v.value,
                    tooltip: v.tooltip,
                    disabled: v.loginRequired && !isLoggedIn,
                    icon: v.loginRequired && !isLoggedIn ? faLock : undefined,
                    iconClassName: "h-2.5",
                }))}
                value={value}
                onValueChange={(next) => onChange(next as T)}
                disabled={disabled}
            />
        </ControlLabel>
    );
}
